/**
 * Scene Tag Selector Component
 * Sprint 19+: Enhanced Scene Cards
 * 
 * Tag system with predefined tag types:
 * - Intro, Content, Demo, CTA, Outro, Transition
 * - Color-coded badges
 * - Click to add/remove tags
 * - Pre-populated from templates
 */

import React, { useState } from 'react';
import { Tag, Modal, Space, Spin } from 'antd';
import { Plus, X, Check, PlayCircle, FileText, Monitor, Megaphone, Flag, ArrowRight } from 'lucide-react';
import { LoadingOutlined } from '@ant-design/icons';
import { SceneTagType } from '../../../../../types/template';
import './SceneTagSelector.scss';

interface SceneTagSelectorProps {
  selectedTags: SceneTagType[];
  onChange: (tags: SceneTagType[]) => void;
  disabled?: boolean;
  maxTags?: number;
  isUpdating?: boolean;
}

// Tag metadata: label, color, description, icon
const TAG_METADATA: Record<SceneTagType, { 
  label: string; 
  color: string; 
  description: string;
  icon: React.ReactNode;
}> = {
  intro: {
    label: 'Intro',
    color: '#6b7280',
    description: 'Opening scene, welcome, introduction',
    icon: <PlayCircle size={16} />
  },
  content: {
    label: 'Content',
    color: '#6b7280',
    description: 'Main content, body of the video',
    icon: <FileText size={16} />
  },
  demo: {
    label: 'Demo',
    color: '#6b7280',
    description: 'Product demonstration, tutorial steps',
    icon: <Monitor size={16} />
  },
  cta: {
    label: 'CTA',
    color: '#6b7280',
    description: 'Call-to-action, closing statements',
    icon: <Megaphone size={16} />
  },
  outro: {
    label: 'Outro',
    color: '#6b7280',
    description: 'Ending scene, farewell, credits',
    icon: <Flag size={16} />
  },
  transition: {
    label: 'Transition',
    color: '#6b7280',
    description: 'Transition or connecting scene',
    icon: <ArrowRight size={16} />
  }
};

const ALL_TAGS: SceneTagType[] = ['intro', 'content', 'demo', 'cta', 'outro', 'transition'];

export const SceneTagSelector: React.FC<SceneTagSelectorProps> = ({
  selectedTags,
  onChange,
  disabled = false,
  maxTags = 3,
  isUpdating = false
}) => {
  const [showModal, setShowModal] = useState(false);
  const [removingTag, setRemovingTag] = useState<SceneTagType | null>(null);

  const handleTagToggle = (tag: SceneTagType) => {
    if (isUpdating) return; // Don't allow changes while updating
    
    console.log('🏷️ Tag toggle clicked:', tag, { 
      isSelected: selectedTags.includes(tag), 
      currentTags: selectedTags,
      maxTags 
    });
    
    if (selectedTags.includes(tag)) {
      // Remove tag
      const newTags = selectedTags.filter(t => t !== tag);
      console.log('🏷️ Removing tag, new tags:', newTags);
      onChange(newTags);
    } else {
      // Add tag (respect maxTags limit)
      if (selectedTags.length < maxTags) {
        const newTags = [...selectedTags, tag];
        console.log('🏷️ Adding tag, new tags:', newTags);
        onChange(newTags);
      } else {
        console.log('🏷️ Cannot add tag - max limit reached');
      }
    }
  };

  const handleRemoveTag = (tag: SceneTagType, e: React.MouseEvent) => {
    e.stopPropagation();
    if (removingTag) return; // Prevent multiple removals
    
    setRemovingTag(tag);
    onChange(selectedTags.filter(t => t !== tag));
  };

  // Clear removingTag state when update completes
  React.useEffect(() => {
    if (!isUpdating && removingTag) {
      setRemovingTag(null);
    }
  }, [isUpdating, removingTag]);

  return (
    <div className="scene-tag-selector">
      <div className="tag-list">
        {selectedTags.map(tag => {
          const isRemoving = removingTag === tag;
          const metadata = TAG_METADATA[tag];
          const displayLabel = metadata?.label || tag.charAt(0).toUpperCase() + tag.slice(1);
          
          return (
            <Tag
              key={tag}
              className={`scene-tag ${isRemoving ? 'removing' : ''}`}
              closable={!disabled}
              onClose={(e) => isRemoving ? e.preventDefault() : handleRemoveTag(tag, e as any)}
              closeIcon={isRemoving ? <LoadingOutlined spin style={{ fontSize: 12, color: '#4A3CFA' }} /> : undefined}
            >
              {displayLabel}
            </Tag>
          );
        })}
        
        {!disabled && selectedTags.length < maxTags && (
          <Tag
            icon={<Plus size={12} />}
            className="add-tag-button"
            onClick={() => setShowModal(true)}
          >
            Add Tag
          </Tag>
        )}
      </div>

      <Modal
        title={
          <div className="modal-title-wrapper">
            <div className="modal-title-icon">
              <Plus size={20} />
            </div>
            <div>
              <div className="modal-title">Select Scene Tags</div>
              <div className="modal-subtitle">Categorize your scene for better organization</div>
            </div>
          </div>
        }
        open={showModal}
        onCancel={() => !isUpdating && setShowModal(false)}
        footer={null}
        width={540}
        className="tag-selector-modal"
        closable={!isUpdating}
        maskClosable={!isUpdating}
      >
        <Spin 
          spinning={isUpdating} 
          indicator={<LoadingOutlined style={{ fontSize: 24, color: '#4A3CFA' }} spin />}
          tip="Saving changes..."
        >
          <div className="tag-selector-content" style={{ opacity: isUpdating ? 0.6 : 1, pointerEvents: isUpdating ? 'none' : 'auto' }}>
            <div className="tag-info">
              <span className="tag-count">{selectedTags.length}/{maxTags}</span> tags selected
            </div>
            
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {ALL_TAGS.map(tag => {
                const isSelected = selectedTags.includes(tag);
                const metadata = TAG_METADATA[tag];
                const canSelect = !isSelected && selectedTags.length < maxTags;

                return (
                  <div
                    key={tag}
                    className={`tag-option ${isSelected ? 'selected' : ''} ${!canSelect && !isSelected ? 'disabled' : ''}`}
                    onClick={() => {
                      console.log('🖱️ Card clicked:', tag, { canSelect, isSelected });
                      if (canSelect || isSelected) {
                        handleTagToggle(tag);
                      } else {
                        console.log('🖱️ Click blocked - canSelect:', canSelect, 'isSelected:', isSelected);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && (canSelect || isSelected)) {
                        e.preventDefault();
                        handleTagToggle(tag);
                      }
                    }}
                  >
                    <div className="tag-option-left">
                      <div className="tag-icon" style={{ color: metadata.color }}>
                        {metadata.icon}
                      </div>
                      <div className="tag-info-wrapper">
                        <div className="tag-label">{metadata.label}</div>
                        <div className="tag-description">{metadata.description}</div>
                      </div>
                    </div>
                    
                    <div className="tag-option-right">
                      {isSelected ? (
                        <div className="selected-checkbox checked">
                          <Check size={14} strokeWidth={3} />
                        </div>
                      ) : (
                        <div className="selected-checkbox"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </Space>
          </div>
        </Spin>
      </Modal>
    </div>
  );
};
